import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Search,
  DollarSign,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Building2,
  MoreVertical,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
  Gift,
  Ban,
  Receipt,
  Sparkles,
  Crown,
  Zap,
  Users,
  ChevronRight,
  Check,
  X,
  Clock,
  AlertTriangle,
  BarChart3,
  PieChart,
  Activity,
  UserMinus,
  UserPlus
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { SortableTableHead, sortData } from '../ui/sortable-table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plan icons and colors. Keys must match the live plan IDs in
// backend/routes/billing.py PLANS — when a new tier is added, add it here
// or it'll fall back to the `unknown` entry (which prevents the page from
// crashing on undefined.icon).
const PLAN_CONFIG = {
  none: { icon: Zap, color: 'slate', label: 'No Plan' },
  free: { icon: Zap, color: 'slate', label: 'Free' },
  starter: { icon: Sparkles, color: 'blue', label: 'Starter' },
  pro: { icon: Crown, color: 'violet', label: 'Pro' },
  professional: { icon: Crown, color: 'violet', label: 'Professional' }, // legacy alias
  business: { icon: Building2, color: 'emerald', label: 'Business' },
  done_for_you: { icon: Users, color: 'teal', label: 'Done For You' },
  platinum: { icon: Crown, color: 'amber', label: 'Platinum CFO' },
  // Defensive fallback — referenced when an unknown plan id arrives so we
  // never throw on `config.icon`.
  unknown: { icon: Zap, color: 'slate', label: 'Unknown' },
};

// Resolve a plan id to its config with a safe fallback. ALWAYS use this
// instead of indexing PLAN_CONFIG directly.
const planConfig = (planId) => PLAN_CONFIG[planId] || PLAN_CONFIG.unknown;

// Status badge styles
const STATUS_STYLES = {
  active: 'bg-emerald-500/20 text-emerald-400',
  trialing: 'bg-blue-500/20 text-blue-400',
  past_due: 'bg-red-500/20 text-red-400',
  canceled: 'bg-muted0/20 text-muted-foreground',
  cancelled: 'bg-muted0/20 text-muted-foreground',
  none: 'bg-white/10 text-muted-foreground',
  inactive: 'bg-white/10 text-muted-foreground',
  paused: 'bg-yellow-500/20 text-yellow-400'
};

// Stat Card Component
const StatCard = ({ title, value, change, icon: Icon, color = 'emerald', prefix = '' }) => (
  <Card className="bg-card border-white/15">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</p>
          {change !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {change >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              <span>{change >= 0 ? '+' : ''}{change}%</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full bg-${color}-500/10`}>
          <Icon className={`h-6 w-6 text-${color}-400`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function OwnerAdminBilling() {
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [cancellations, setCancellations] = useState(null);
  const [cxlWindowDays, setCxlWindowDays] = useState(90);
  const [cxlFilter, setCxlFilter] = useState('all'); // all | in_trial | post_trial
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'created_at', direction: 'desc' });
  
  // Dialog states
  const [changePlanDialog, setChangePlanDialog] = useState({ open: false, subscription: null });
  const [refundDialog, setRefundDialog] = useState({ open: false, subscription: null });
  const [creditDialog, setCreditDialog] = useState({ open: false, subscription: null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, subscription: null });
  
  // Form states
  const [selectedPlan, setSelectedPlan] = useState('');
  const [planChangeReason, setPlanChangeReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch subscriptions
      let url = `${API_URL}/api/owner-admin/billing/subscriptions?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (planFilter !== 'all') url += `plan=${planFilter}&`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      
      const [subsRes, plansRes, revenueRes, analyticsRes] = await Promise.all([
        fetch(url, { credentials: 'include' }),
        fetch(`${API_URL}/api/owner-admin/billing/plans`, { credentials: 'include' }),
        fetch(`${API_URL}/api/owner-admin/billing/revenue`, { credentials: 'include' }),
        fetch(`${API_URL}/api/owner-admin/billing/analytics?months=6`, { credentials: 'include' })
      ]);
      
      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubscriptions(data.subscriptions || []);
        setStats(data.stats);
      }
      
      if (plansRes.ok) {
        const data = await plansRes.json();
        setPlans(data.plans || {});
      }
      
      if (revenueRes.ok) {
        const data = await revenueRes.json();
        setStats(prev => ({ ...prev, ...data }));
      }
      
      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [search, planFilter, statusFilter]);

  // Cancellation analytics fetcher — kept separate from the main billing
  // fetcher because (a) different cadence — admins eyeball it less often —
  // and (b) the filters (window, in-trial vs post-trial) are independent
  // from the subscriptions list above.
  const fetchCancellations = useCallback(async () => {
    if (activeTab !== 'cancellations') return;
    try {
      let url = `${API_URL}/api/owner-admin/billing/cancellations?days=${cxlWindowDays}&limit=200`;
      if (cxlFilter === 'in_trial') url += '&in_trial=true';
      if (cxlFilter === 'post_trial') url += '&in_trial=false';
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        setCancellations(await res.json());
      }
    } catch (e) {
      console.error('Failed to load cancellations:', e);
    }
  }, [activeTab, cxlWindowDays, cxlFilter]);

  useEffect(() => {
    fetchCancellations();
  }, [fetchCancellations]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchData]);

  const handleChangePlan = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/billing/subscriptions/${changePlanDialog.subscription.workspace_id}/change-plan`, {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            new_plan: selectedPlan,
            reason: planChangeReason,
            prorate: true
          })
        }
      );
      
      if (response.ok) {
        toast.success('Plan changed successfully');
        setChangePlanDialog({ open: false, subscription: null });
        setSelectedPlan('');
        setPlanChangeReason('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change plan');
      }
    } catch (error) {
      toast.error('Failed to change plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefund = async () => {
    const amount = parseFloat(refundAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!refundReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/billing/subscriptions/${refundDialog.subscription.workspace_id}/refund`, {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: amount,
            reason: refundReason
          })
        }
      );
      
      if (response.ok) {
        toast.success(`Refund of $${amount.toFixed(2)} issued`);
        setRefundDialog({ open: false, subscription: null });
        setRefundAmount('');
        setRefundReason('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to issue refund');
      }
    } catch (error) {
      toast.error('Failed to issue refund');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCredit = async () => {
    const amount = parseFloat(creditAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!creditReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/billing/subscriptions/${creditDialog.subscription.workspace_id}/credit`, {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: amount,
            reason: creditReason
          })
        }
      );
      
      if (response.ok) {
        toast.success(`Credit of $${amount.toFixed(2)} applied`);
        setCreditDialog({ open: false, subscription: null });
        setCreditAmount('');
        setCreditReason('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to apply credit');
      }
    } catch (error) {
      toast.error('Failed to apply credit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/billing/subscriptions/${cancelDialog.subscription.workspace_id}/cancel?reason=${encodeURIComponent(cancelReason)}&immediate=${cancelImmediate}`, {
          credentials: 'include',
          method: 'POST'
        }
      );
      
      if (response.ok) {
        toast.success('Subscription canceled');
        setCancelDialog({ open: false, subscription: null });
        setCancelReason('');
        setCancelImmediate(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getPlanBadge = (plan) => {
    const config = planConfig(plan);
    const Icon = config.icon;
    return (
      <Badge className={`bg-${config.color}-500/20 text-${config.color}-400`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading && !subscriptions.length) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="bg-card border-white/15">
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-24 bg-white/10" />
                <Skeleton className="h-8 w-32 mt-2 bg-white/10" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Billing & Subscriptions</h1>
          <p className="text-muted-foreground">Manage customer subscriptions and revenue</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="border-white/20 text-muted-foreground/80">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Revenue Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Monthly Recurring Revenue"
          value={stats?.mrr || 0}
          prefix="$"
          icon={DollarSign}
          color="emerald"
        />
        <StatCard
          title="Active Subscriptions"
          value={stats?.active_subscriptions || stats?.active_count || 0}
          icon={CreditCard}
          color="blue"
        />
        <StatCard
          title="Revenue (30d)"
          value={stats?.revenue_30d || 0}
          prefix="$"
          icon={TrendingUp}
          color="violet"
        />
        <StatCard
          title="Refunds (30d)"
          value={stats?.refunds_30d || 0}
          prefix="$"
          icon={Receipt}
          color="red"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card border border-white/15">
          <TabsTrigger value="subscriptions" className="data-[state=active]:bg-white/10" data-testid="tab-subscriptions">
            Subscriptions
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-white/10" data-testid="tab-analytics">
            <BarChart3 className="h-4 w-4 mr-1" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="cancellations" className="data-[state=active]:bg-white/10" data-testid="tab-cancellations">
            <UserMinus className="h-4 w-4 mr-1" />
            Cancellations
          </TabsTrigger>
          <TabsTrigger value="plans" className="data-[state=active]:bg-white/10" data-testid="tab-plans">
            Plans
          </TabsTrigger>
        </TabsList>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="mt-6 space-y-4">
          {/* Filters */}
          <Card className="bg-card border-white/15">
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by company name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-white/10 border-white/20 text-white"
                    data-testid="billing-search"
                  />
                </div>
                <Select value={planFilter} onValueChange={setPlanFilter}>
                  <SelectTrigger className="w-[150px] bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px] bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trialing">Trialing</SelectItem>
                    <SelectItem value="past_due">Past Due</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Subscriptions Table */}
          <Card className="bg-card border-white/15">
            <CardContent className="p-0">
              {subscriptions.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No subscriptions found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/15 hover:bg-white/[0.07]">
                      <SortableTableHead sortKey="workspace_name" currentSort={sort} onSort={setSort}>
                        Customer
                      </SortableTableHead>
                      <SortableTableHead 
                        sortKey="plan" 
                        currentSort={sort} 
                        onSort={setSort}
                        filterOptions={Object.entries(PLAN_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
                        currentFilter={planFilter !== 'all' ? planFilter : null}
                        onFilter={(val) => setPlanFilter(val || 'all')}
                      >
                        Plan
                      </SortableTableHead>
                      <SortableTableHead 
                        sortKey="status" 
                        currentSort={sort} 
                        onSort={setSort}
                        filterOptions={Object.keys(STATUS_STYLES).map(k => ({ value: k, label: k.charAt(0).toUpperCase() + k.slice(1).replace('_', ' ') }))}
                        currentFilter={statusFilter !== 'all' ? statusFilter : null}
                        onFilter={(val) => setStatusFilter(val || 'all')}
                      >
                        Status
                      </SortableTableHead>
                      <SortableTableHead sortKey="plan" currentSort={sort} onSort={setSort}>
                        Price
                      </SortableTableHead>
                      <SortableTableHead sortKey="created_at" currentSort={sort} onSort={setSort}>
                        Since
                      </SortableTableHead>
                      <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortData(subscriptions, sort).map((sub) => (
                      <TableRow
                        key={sub.workspace_id}
                        className="border-white/15 hover:bg-white/[0.07]"
                        data-testid={`subscription-row-${sub.workspace_id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-emerald-500/10">
                              <Building2 className="h-4 w-4 text-emerald-400" />
                            </div>
                            <div>
                              <p className="font-medium text-white">{sub.workspace_name}</p>
                              <p className="text-xs text-muted-foreground">{sub.workspace_id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getPlanBadge(sub.plan)}</TableCell>
                        <TableCell>
                          <Badge className={STATUS_STYLES[sub.status] || STATUS_STYLES.none}>
                            {sub.status || 'No plan'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-white font-mono">
                          {formatCurrency(plans[sub.plan]?.price || 0)}/mo
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(sub.current_period_start || sub.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-muted-foreground">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/owner-admin/customers/${sub.workspace_id}`}>
                                  <Building2 className="h-4 w-4 mr-2" />
                                  View Customer
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => {
                                setChangePlanDialog({ open: true, subscription: sub });
                                setSelectedPlan(sub.plan);
                              }}>
                                <ArrowUpCircle className="h-4 w-4 mr-2" />
                                Change Plan
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setCreditDialog({ open: true, subscription: sub })}>
                                <Gift className="h-4 w-4 mr-2" />
                                Apply Credit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setRefundDialog({ open: true, subscription: sub })}>
                                <Receipt className="h-4 w-4 mr-2" />
                                Issue Refund
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-400"
                                onClick={() => setCancelDialog({ open: true, subscription: sub })}
                                disabled={sub.status === 'canceled'}
                              >
                                <Ban className="h-4 w-4 mr-2" />
                                Cancel Subscription
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {Object.entries(plans).map(([key, plan]) => {
              const config = planConfig(key);
              const Icon = config.icon;
              const count = stats?.plan_distribution?.[key] || 0;
              
              return (
                <Card key={key} className="bg-card border-white/15">
                  <CardHeader>
                    <div className={`p-3 rounded-lg bg-${config.color}-500/10 w-fit`}>
                      <Icon className={`h-6 w-6 text-${config.color}-400`} />
                    </div>
                    <CardTitle className="text-white">{plan.name}</CardTitle>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-white">${plan.price}</span>
                      <span className="text-muted-foreground">/{plan.billing_period}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <Badge variant="outline" className="border-white/20 text-muted-foreground">
                        <Users className="h-3 w-3 mr-1" />
                        {count} customers
                      </Badge>
                    </div>
                    <ul className="space-y-2">
                      {plan.features?.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground/80">
                          <Check className="h-4 w-4 text-emerald-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-6 space-y-6">
          {/* Analytics Metrics */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="bg-card border-white/15">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">MRR Growth</p>
                    <p className="text-2xl font-bold text-white mt-1">
                      {analytics?.metrics?.mrr_growth >= 0 ? '+' : ''}{analytics?.metrics?.mrr_growth || 0}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">vs last month</p>
                  </div>
                  <div className={`p-3 rounded-full ${(analytics?.metrics?.mrr_growth || 0) >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                    {(analytics?.metrics?.mrr_growth || 0) >= 0 
                      ? <TrendingUp className="h-6 w-6 text-emerald-400" />
                      : <TrendingDown className="h-6 w-6 text-red-400" />
                    }
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-white/15">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Churn Rate</p>
                    <p className="text-2xl font-bold text-white mt-1">{analytics?.metrics?.churn_rate || 0}%</p>
                    <p className="text-xs text-muted-foreground mt-1">last 30 days</p>
                  </div>
                  <div className="p-3 rounded-full bg-red-500/10">
                    <UserMinus className="h-6 w-6 text-red-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-white/15">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Upgrades (90d)</p>
                    <p className="text-2xl font-bold text-white mt-1">{analytics?.metrics?.upgrades_90d || 0}</p>
                    <p className="text-xs text-emerald-400 mt-1">plan upgrades</p>
                  </div>
                  <div className="p-3 rounded-full bg-emerald-500/10">
                    <ArrowUpCircle className="h-6 w-6 text-emerald-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-white/15">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Downgrades (90d)</p>
                    <p className="text-2xl font-bold text-white mt-1">{analytics?.metrics?.downgrades_90d || 0}</p>
                    <p className="text-xs text-yellow-400 mt-1">plan downgrades</p>
                  </div>
                  <div className="p-3 rounded-full bg-yellow-500/10">
                    <ArrowDownCircle className="h-6 w-6 text-yellow-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 1 */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* MRR Trend Chart */}
            <Card className="bg-card border-white/15">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="h-5 w-5 text-emerald-400" />
                  MRR Trend
                </CardTitle>
                <CardDescription className="text-muted-foreground">Monthly recurring revenue over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics?.mrr_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        labelStyle={{ color: '#f1f5f9' }}
                        formatter={(value) => [`$${value}`, 'MRR']}
                      />
                      <Line
                        type="monotone"
                        dataKey="mrr"
                        stroke="#10b981"
                        strokeWidth={3}
                        dot={{ fill: '#10b981', strokeWidth: 2 }}
                        activeDot={{ r: 6, fill: '#10b981' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Customer Growth Chart */}
            <Card className="bg-card border-white/15">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-blue-400" />
                  Customer Growth
                </CardTitle>
                <CardDescription className="text-muted-foreground">Total and new customers per month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics?.customer_growth || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        labelStyle={{ color: '#f1f5f9' }}
                      />
                      <Legend />
                      <Bar dataKey="total" name="Total Customers" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="new" name="New Customers" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Plan Distribution Pie Chart */}
            <Card className="bg-card border-white/15">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-violet-400" />
                  Plan Distribution
                </CardTitle>
                <CardDescription className="text-muted-foreground">Customers by subscription plan</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={analytics?.plan_distribution || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="count"
                        nameKey="plan"
                        label={({ plan, count }) => `${plan}: ${count}`}
                        labelLine={{ stroke: '#64748b' }}
                      >
                        {(analytics?.plan_distribution || []).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={['#64748b', '#3b82f6', '#8b5cf6', '#10b981'][index % 4]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        formatter={(value, name) => [value, name]}
                      />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Revenue by Plan */}
            <Card className="bg-card border-white/15">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-400" />
                  Revenue by Plan
                </CardTitle>
                <CardDescription className="text-muted-foreground">Monthly revenue contribution by plan</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics?.plan_distribution || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis type="number" stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `$${v}`} />
                      <YAxis type="category" dataKey="plan" stroke="#94a3b8" fontSize={12} width={100} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        labelStyle={{ color: '#f1f5f9' }}
                        formatter={(value) => [`$${value}`, 'Revenue']}
                      />
                      <Bar dataKey="revenue" fill="#10b981" radius={[0, 4, 4, 0]}>
                        {(analytics?.plan_distribution || []).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={['#64748b', '#3b82f6', '#8b5cf6', '#10b981'][index % 4]} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Net Movement Summary */}
          <Card className="bg-card border-white/15">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-white">Net Plan Movement (90 days)</h3>
                  <p className="text-muted-foreground text-sm mt-1">
                    {analytics?.metrics?.upgrades_90d || 0} upgrades vs {analytics?.metrics?.downgrades_90d || 0} downgrades
                  </p>
                </div>
                <div className={`flex items-center gap-2 text-2xl font-bold ${
                  (analytics?.metrics?.net_plan_movement || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {(analytics?.metrics?.net_plan_movement || 0) >= 0 ? '+' : ''}
                  {analytics?.metrics?.net_plan_movement || 0}
                  {(analytics?.metrics?.net_plan_movement || 0) >= 0 
                    ? <TrendingUp className="h-6 w-6" />
                    : <TrendingDown className="h-6 w-6" />
                  }
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cancellations Tab — sourced from /api/owner-admin/billing/cancellations
            which is populated by the Stripe webhook on cancel events. */}
        <TabsContent value="cancellations" className="mt-6 space-y-6" data-testid="cancellations-tab-content">
          {/* Filters */}
          <Card className="bg-card border-white/15">
            <CardContent className="pt-6 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Window</span>
                <Select value={String(cxlWindowDays)} onValueChange={(v) => setCxlWindowDays(Number(v))}>
                  <SelectTrigger className="w-32 bg-background border-white/15 text-white" data-testid="cxl-window-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="180">Last 180 days</SelectItem>
                    <SelectItem value="365">Last 365 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Filter</span>
                <Select value={cxlFilter} onValueChange={setCxlFilter}>
                  <SelectTrigger className="w-44 bg-background border-white/15 text-white" data-testid="cxl-filter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All cancellations</SelectItem>
                    <SelectItem value="in_trial">During free trial</SelectItem>
                    <SelectItem value="post_trial">After paying (post-trial)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-auto text-xs text-muted-foreground" data-testid="cxl-meta">
                Window: <span className="text-white font-mono">{cancellations?.window_days || cxlWindowDays}d</span>
                {' · '}Showing <span className="text-white font-mono">{cancellations?.rows?.length || 0}</span> of <span className="text-white font-mono">{cancellations?.filtered_total || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* KPI tiles */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="bg-card border-white/15" data-testid="cxl-kpi-total">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total cancellations</p>
                <p className="text-3xl font-bold text-white mt-1">{cancellations?.totals?.total ?? 0}</p>
                <p className="text-xs text-muted-foreground mt-1">in window</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-white/15" data-testid="cxl-kpi-trial">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">During free trial</p>
                <p className="text-3xl font-bold text-amber-300 mt-1">{cancellations?.totals?.in_trial ?? 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {cancellations?.totals?.trial_churn_pct ?? 0}% of cancellations
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card border-white/15" data-testid="cxl-kpi-paid">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">After paying</p>
                <p className="text-3xl font-bold text-rose-300 mt-1">{cancellations?.totals?.post_trial ?? 0}</p>
                <p className="text-xs text-muted-foreground mt-1">paid customers lost</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-white/15" data-testid="cxl-kpi-mrr">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Lost MRR</p>
                <p className="text-3xl font-bold text-white mt-1">
                  ${(cancellations?.totals?.lost_mrr_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-muted-foreground mt-1">monthly recurring</p>
              </CardContent>
            </Card>
          </div>

          {/* Daily series chart */}
          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Activity className="h-4 w-4" /> Daily cancellation volume
              </CardTitle>
              <CardDescription>Stacked by trial vs post-trial.</CardDescription>
            </CardHeader>
            <CardContent>
              {(cancellations?.daily_series || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No cancellations recorded in this window — that&apos;s good news.</p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cancellations.daily_series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                      <XAxis dataKey="date" stroke="#ffffff60" fontSize={11} />
                      <YAxis stroke="#ffffff60" fontSize={11} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: '#0c0a09', border: '1px solid #ffffff20', borderRadius: 8 }}
                        labelStyle={{ color: '#fff' }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="in_trial" stackId="a" fill="#fbbf24" name="During trial" />
                      <Bar dataKey="post_trial" stackId="a" fill="#fb7185" name="Post-trial" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Breakdown rows */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="bg-card border-white/15">
              <CardHeader>
                <CardTitle className="text-white text-base">By plan</CardTitle>
              </CardHeader>
              <CardContent>
                {(cancellations?.plan_breakdown || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No data.</p>
                ) : (
                  <ul className="space-y-2">
                    {cancellations.plan_breakdown.map((p) => (
                      <li key={p.plan_id} className="flex items-center justify-between text-sm" data-testid={`cxl-plan-${p.plan_id}`}>
                        <span className="text-white">{p.plan_name}</span>
                        <span className="font-mono text-muted-foreground">
                          {p.count} · ${p.lost_mrr_usd?.toLocaleString(undefined, { maximumFractionDigits: 0 })} MRR
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
            <Card className="bg-card border-white/15">
              <CardHeader>
                <CardTitle className="text-white text-base">Top reasons</CardTitle>
                <CardDescription>From Stripe&apos;s cancellation survey + reason field.</CardDescription>
              </CardHeader>
              <CardContent>
                {(cancellations?.reason_breakdown || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No reasons recorded yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {cancellations.reason_breakdown.map((r) => (
                      <li key={r.reason} className="flex items-center justify-between text-sm">
                        <span className="text-white capitalize">{(r.reason || 'not_specified').replace(/_/g, ' ')}</span>
                        <span className="font-mono text-muted-foreground">{r.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Row table */}
          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white text-base">Recent cancellations</CardTitle>
              <CardDescription>Click a row in customers / users to drill in.</CardDescription>
            </CardHeader>
            <CardContent>
              {(cancellations?.rows || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Nothing here yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-muted-foreground">Customer</TableHead>
                        <TableHead className="text-muted-foreground">Plan</TableHead>
                        <TableHead className="text-muted-foreground">Trial?</TableHead>
                        <TableHead className="text-muted-foreground">Days</TableHead>
                        <TableHead className="text-muted-foreground">Lost MRR</TableHead>
                        <TableHead className="text-muted-foreground">Kind</TableHead>
                        <TableHead className="text-muted-foreground">When</TableHead>
                        <TableHead className="text-muted-foreground">Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cancellations.rows.map((r) => (
                        <TableRow key={r.cancellation_id} className="border-white/5" data-testid={`cxl-row-${r.cancellation_id}`}>
                          <TableCell>
                            <p className="text-white text-sm">{r.name || '—'}</p>
                            <p className="text-xs text-muted-foreground">{r.email || r.user_id}</p>
                          </TableCell>
                          <TableCell>
                            <span className="text-white text-sm">{r.plan_name || r.plan_id || '—'}</span>
                            <span className="text-xs text-muted-foreground ml-1">· {r.cadence}</span>
                          </TableCell>
                          <TableCell>
                            {r.was_in_trial ? (
                              <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">In trial</Badge>
                            ) : (
                              <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30">Paid</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-white font-mono text-sm">{r.days_subscribed ?? '—'}</TableCell>
                          <TableCell className="text-white font-mono text-sm">${(r.lost_mrr_usd || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="border-white/15 text-muted-foreground">
                              {r.event_kind === 'deleted' ? 'Ended' : 'Scheduled'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {(r.cancel_reason || r.cancel_feedback || '').replace(/_/g, ' ') || '—'}
                            {r.cancel_comment && (
                              <p className="italic text-white/60 mt-1 max-w-[16rem] truncate">&ldquo;{r.cancel_comment}&rdquo;</p>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Change Plan Dialog */}
      <Dialog open={changePlanDialog.open} onOpenChange={(open) => !open && setChangePlanDialog({ open: false, subscription: null })}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Change Subscription Plan</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Change plan for <strong className="text-white">{changePlanDialog.subscription?.workspace_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Current Plan</label>
              <div>{getPlanBadge(changePlanDialog.subscription?.plan)}</div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">New Plan</label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(plans).map(([key, plan]) => (
                    <SelectItem key={key} value={key} disabled={key === changePlanDialog.subscription?.plan}>
                      {plan.name} - ${plan.price}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Reason (optional)</label>
              <Textarea
                placeholder="e.g., Customer requested upgrade"
                value={planChangeReason}
                onChange={(e) => setPlanChangeReason(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setChangePlanDialog({ open: false, subscription: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleChangePlan}
              disabled={submitting || !selectedPlan || selectedPlan === changePlanDialog.subscription?.plan}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Changing...' : 'Change Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialog.open} onOpenChange={(open) => !open && setRefundDialog({ open: false, subscription: null })}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Issue Refund</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Issue refund for <strong className="text-white">{refundDialog.subscription?.workspace_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
                <div className="text-sm text-yellow-200">
                  <p className="font-medium">This action is logged</p>
                  <p>All refunds are recorded in the audit log.</p>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Amount *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="pl-10 bg-white/10 border-white/20 text-white"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Reason *</label>
              <Textarea
                placeholder="e.g., Customer charged twice"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRefundDialog({ open: false, subscription: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleRefund}
              disabled={submitting || !refundAmount || !refundReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? 'Processing...' : 'Issue Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Dialog */}
      <Dialog open={creditDialog.open} onOpenChange={(open) => !open && setCreditDialog({ open: false, subscription: null })}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Apply Account Credit</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Apply credit for <strong className="text-white">{creditDialog.subscription?.workspace_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Credit Amount *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  className="pl-10 bg-white/10 border-white/20 text-white"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Reason *</label>
              <Textarea
                placeholder="e.g., Loyalty bonus, service compensation"
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreditDialog({ open: false, subscription: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleCredit}
              disabled={submitting || !creditAmount || !creditReason.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Applying...' : 'Apply Credit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(open) => !open && setCancelDialog({ open: false, subscription: null })}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Cancel Subscription</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Cancel subscription for <strong className="text-white">{cancelDialog.subscription?.workspace_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
                <div className="text-sm text-red-200">
                  <p className="font-medium">Warning</p>
                  <p>This will cancel the customer&apos;s subscription. They may lose access to paid features.</p>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Cancellation Reason *</label>
              <Textarea
                placeholder="e.g., Customer request, non-payment"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="cancelImmediate"
                checked={cancelImmediate}
                onChange={(e) => setCancelImmediate(e.target.checked)}
                className="rounded border-white/20"
              />
              <label htmlFor="cancelImmediate" className="text-sm text-muted-foreground/80">
                Cancel immediately (do not wait for billing period end)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelDialog({ open: false, subscription: null })}>
              Keep Subscription
            </Button>
            <Button
              onClick={handleCancel}
              disabled={submitting || !cancelReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? 'Canceling...' : 'Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
